import * as React from "react";
import { Square } from "lucide-react";
import { Button } from "../primitives/Button";
import { cn } from "../lib/cn";

// Component-scoped keyframe (the house pattern: see border-beam / AikoSurfaces).
// The source animated bar heights off `Date.now()` in render, which never moved;
// this is a real CSS height pulse, disabled under prefers-reduced-motion.
const WAVE_STYLE = `
@keyframes aisquare-listening-wave {
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
}
.aisquare-listening-bar {
  transform-origin: center;
  animation: aisquare-listening-wave 0.9s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .aisquare-listening-bar { animation: none; transform: scaleY(0.7); }
}
`;

export interface ListeningOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stop / cancel handler for the active recording. */
  onStop: () => void;
  /** Live transcript; falls back to `placeholder` while empty. */
  transcript?: string;
  /** Shown when there is no transcript yet. Default `"Listening..."`. */
  placeholder?: string;
  /** Number of waveform bars. Default `4`. */
  bars?: number;
  /** Aria label for the stop button. Default `"Stop recording"`. */
  stopLabel?: string;
}

/**
 * ListeningOverlay — the active voice-input affordance: an animated waveform, a
 * live transcript (or a "Listening..." placeholder), and a stop button (ported
 * library-clean from the AI-Expert chat).
 *
 * The container is an `aria-live="polite"` status region so the transcript is
 * announced as it streams; the waveform bars are decorative (`aria-hidden`) and
 * pulse via a component-scoped keyframe that is disabled under
 * `prefers-reduced-motion`. Brand tokens only.
 */
export const ListeningOverlay = React.forwardRef<HTMLDivElement, ListeningOverlayProps>(
  (
    {
      onStop,
      transcript,
      placeholder = "Listening...",
      bars = 4,
      stopLabel = "Stop recording",
      className,
      ...rest
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          "relative flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md",
          className,
        )}
        {...rest}
      >
        <style>{WAVE_STYLE}</style>
        <div className="flex flex-1 items-center gap-4">
          <div className="flex h-6 items-center gap-1" aria-hidden="true">
            {Array.from({ length: Math.max(1, bars) }).map((_, i) => (
              <span
                key={i}
                className="aisquare-listening-bar h-5 w-1 rounded-full bg-primary"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
          <span className="font-medium text-foreground">{transcript || placeholder}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 rounded-full text-muted-foreground hover:text-foreground"
          onClick={onStop}
          aria-label={stopLabel}
        >
          <Square className="size-4 fill-current" />
        </Button>
      </div>
    );
  },
);

ListeningOverlay.displayName = "ListeningOverlay";
